import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  canHaveChildren,
} from './viewTreeUtils';

const SortableTreeNode = ({ 
  node, 
  depth, 
  selectedViewId, 
  onSelectView, 
  onAddView, 
  onRemoveView, 
  onDuplicateView,
  expandedNodes,
  toggleExpand,
  isExpanded,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasChildren = node.children && node.children.length > 0;
  const isContainer = canHaveChildren(node.type);
  const expanded = isExpanded(node.id);
  const selected = selectedViewId === node.id;

  return (
    <div ref={setNodeRef} style={style} className="select-none">
      <div
        className={`
          flex items-center gap-2 px-2 py-1 rounded cursor-pointer
          ${selected ? 'bg-gradient-primary text-white' : 'hover:bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}
        `}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onClick={(e) => {
          e.stopPropagation();
          onSelectView(node.id);
        }}
      >
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          style={{ touchAction: 'none' }}
        >
          ⋮⋮
        </div>

        {/* Expand/Collapse Icon */}
        {isContainer && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(node.id);
            }}
            className="w-4 h-4 flex items-center justify-center text-xs"
          >
            {expanded ? '▼' : '▶'}
          </button>
        )}
        {!isContainer && <div className="w-4 h-4" />}

        {/* View Type Icon */}
        <span className="text-xs font-mono">
          {node.type === 'HStack' && '→'}
          {node.type === 'VStack' && '↓'}
          {node.type === 'ZStack' && '⊞'}
          {node.type === 'Text' && 'T'}
          {node.type === 'Button' && 'B'}
          {node.type === 'Image' && '🖼'}
          {node.type === 'Spacer' && '␣'}
          {node.type === 'Divider' && '─'}
          {node.type === 'List' && '☰'}
          {node.type === 'ScrollView' && '⇅'}
          {!['HStack', 'VStack', 'ZStack', 'Text', 'Button', 'Image', 'Spacer', 'Divider', 'List', 'ScrollView'].includes(node.type) && '•'}
        </span>

        {/* View Type Label */}
        <span className="text-sm font-medium flex-1">{node.type}</span>

        {/* Quick Info */}
        {node.type === 'Text' && node.properties?.content && (
          <span className="text-xs opacity-75 truncate max-w-[100px]">
            "{node.properties.content.substring(0, 15)}"
          </span>
        )}
        {node.type === 'Button' && node.properties?.title && (
          <span className="text-xs opacity-75 truncate max-w-[100px]">
            "{node.properties.title.substring(0, 15)}"
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {isContainer && (
            <button
              onClick={() => onAddView(node.id)}
              className="px-2 py-1 text-xs bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 rounded"
              title="Add child view"
            >
              +
            </button>
          )}
          <button
            onClick={() => onDuplicateView(node.id)}
            className="px-2 py-1 text-xs bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 rounded"
            title="Duplicate"
          >
            ⧉
          </button>
          <button
            onClick={() => onRemoveView(node.id)}
            className="px-2 py-1 text-xs bg-red-600 hover:bg-red-500 rounded"
            title="Delete"
          >
            ×
          </button>
        </div>
      </div>

      {/* Children */}
      {isContainer && expanded && hasChildren && (
        <SortableTreeNodes
          nodes={node.children}
          depth={depth + 1}
          selectedViewId={selectedViewId}
          onSelectView={onSelectView}
          onAddView={onAddView}
          onRemoveView={onRemoveView}
          onDuplicateView={onDuplicateView}
          expandedNodes={expandedNodes}
          toggleExpand={toggleExpand}
          isExpanded={isExpanded}
          parentId={node.id}
        />
      )}
    </div>
  );
};

const SortableTreeNodes = ({
  nodes,
  depth,
  selectedViewId,
  onSelectView,
  onAddView,
  onRemoveView,
  onDuplicateView,
  expandedNodes,
  toggleExpand,
  isExpanded,
  parentId,
}) => {
  const nodeIds = nodes.map(node => node.id);

  return (
    <SortableContext items={nodeIds} strategy={verticalListSortingStrategy}>
      {nodes.map((node) => (
        <SortableTreeNode
          key={node.id}
          node={node}
          depth={depth}
          selectedViewId={selectedViewId}
          onSelectView={onSelectView}
          onAddView={onAddView}
          onRemoveView={onRemoveView}
          onDuplicateView={onDuplicateView}
          expandedNodes={expandedNodes}
          toggleExpand={toggleExpand}
          isExpanded={isExpanded}
        />
      ))}
    </SortableContext>
  );
};

const ViewTree = ({ viewTree, selectedViewId, onSelectView, onAddView, onRemoveView, onDuplicateView, onMoveView }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleExpand = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const isExpanded = (nodeId) => expandedNodes.has(nodeId);

  // Helper to find node and its parent
  const findNodeInTree = (nodeId, tree, parentId = null, path = []) => {
    for (let i = 0; i < tree.length; i++) {
      if (tree[i].id === nodeId) {
        return { node: tree[i], parentId, index: i };
      }
      if (tree[i].children && tree[i].children.length > 0) {
        const found = findNodeInTree(nodeId, tree[i].children, tree[i].id, [...path, i]);
        if (found) return found;
      }
    }
    return null;
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const activeLocation = findNodeInTree(active.id, viewTree);
    const overLocation = findNodeInTree(over.id, viewTree);

    if (!activeLocation || !overLocation) {
      return;
    }

    // Determine new parent and index
    let newParentId = null;
    let newIndex = 0;

    // Check if we're dropping on a container that can have children
    if (overLocation.node && canHaveChildren(overLocation.node.type)) {
      // Drop inside the container
      newParentId = overLocation.node.id;
      newIndex = overLocation.node.children ? overLocation.node.children.length : 0;
    } else {
      // Drop as sibling
      newParentId = overLocation.parentId;
      newIndex = overLocation.index;
      
      // If we're moving within the same parent and the active item is before the over item,
      // we need to adjust the index
      if (activeLocation.parentId === newParentId && activeLocation.index < overLocation.index) {
        newIndex += 1;
      }
    }

    // Don't allow dropping into itself or its descendants
    if (active.id === newParentId) {
      return;
    }

    // Call the move handler
    if (onMoveView) {
      onMoveView(active.id, newParentId, newIndex);
    }
  };

  const activeNode = activeId ? (() => {
    const findNode = (nodes, id) => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
          const found = findNode(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };
    return findNode(viewTree, activeId);
  })() : null;

  const rootNodeIds = viewTree.map(node => node.id);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          View Tree
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => onAddView(null)}
            className="px-3 py-1 text-xs bg-gradient-primary bg-gradient-primary-hover rounded text-white"
          >
            + Add Root View
          </button>
        </div>
      </div>

      {viewTree.length === 0 ? (
        <div className="text-center py-8 text-gray-600 dark:text-gray-500 text-sm">
          <p>No views yet. Click "Add Root View" to start building.</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-100 dark:bg-gray-800 p-2 max-h-96 overflow-y-auto">
            <SortableContext items={rootNodeIds} strategy={verticalListSortingStrategy}>
              {viewTree.map((node) => (
                <SortableTreeNode
                  key={node.id}
                  node={node}
                  depth={0}
                  selectedViewId={selectedViewId}
                  onSelectView={onSelectView}
                  onAddView={onAddView}
                  onRemoveView={onRemoveView}
                  onDuplicateView={onDuplicateView}
                  expandedNodes={expandedNodes}
                  toggleExpand={toggleExpand}
                  isExpanded={isExpanded}
                />
              ))}
            </SortableContext>
          </div>
          <DragOverlay>
            {activeNode ? (
              <div className="flex items-center gap-2 px-2 py-1 rounded bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 opacity-80">
                <span className="text-xs font-mono">
                  {activeNode.type === 'HStack' && '→'}
                  {activeNode.type === 'VStack' && '↓'}
                  {activeNode.type === 'ZStack' && '⊞'}
                  {activeNode.type === 'Text' && 'T'}
                  {activeNode.type === 'Button' && 'B'}
                  {activeNode.type === 'Image' && '🖼'}
                  {!['HStack', 'VStack', 'ZStack', 'Text', 'Button', 'Image'].includes(activeNode.type) && '•'}
                </span>
                <span className="text-sm font-medium">{activeNode.type}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Add View Modal/Selector */}
      {viewTree.length > 0 && (
        <div className="text-xs text-gray-600 dark:text-gray-400">
          <p>Tip: Drag and drop views to reorder. Click a view to select and edit its properties</p>
        </div>
      )}
    </div>
  );
};

export default ViewTree;
